

import React from 'react';

const COOFloatingButton = ({ isVisible = false }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const iframeRef = React.useRef(null);
  const isVisibleRef = React.useRef(isVisible);
  const prevIsVisibleRef = React.useRef();

  // Handle postMessage from iframe with strict state tracking to prevent any blinking
  React.useEffect(() => {
    let lastProcessTime = 0;
    const handleMessage = (event) => {
      if (iframeRef.current?.contentWindow !== event.source) {
        return;
      }

      if (event.data.type === 'RESIZE_IFRAME' && isVisibleRef.current) {
        const now = Date.now();
        if (now - lastProcessTime < 100) return; // Debounce rapid calls
        lastProcessTime = now;

        const newWidth = event.data.width;
        const newIsExpanded = newWidth > 88;

        // Only update state if the expansion state actually changed
        // This prevents any unnecessary re-renders that cause blinking
        setIsExpanded(currentExpanded => {
          if (currentExpanded !== newIsExpanded) {
            console.log('State changed:', { from: currentExpanded, to: newIsExpanded, width: newWidth });
            return newIsExpanded;
          }
          return currentExpanded; // No change, prevent re-render
        });
      } else if (event.data.type === 'REQUEST_AUTH_TOKEN') {
        // Handle auth token request from iframe
        // Get token from auth context or local storage (matching main app's token key)
        const token = localStorage.getItem('auth_token') ||
          sessionStorage.getItem('auth_token') ||
          window.authToken; // Fallback to global if available

        if (token) {
          event.source.postMessage({
            type: 'AUTH_TOKEN',
            token: token
          }, event.origin);
        } else {
          console.warn('No auth token available for COO chat iframe');
        }
      } else if (event.data.type === 'REQUEST_API_URL') {
        // Handle API URL request from iframe
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        event.source.postMessage({
          type: 'API_URL',
          url: apiUrl
        }, event.origin);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Update visibility ref without causing re-renders
  React.useEffect(() => {
    if (prevIsVisibleRef.current !== isVisible) {
      prevIsVisibleRef.current = isVisible;
      isVisibleRef.current = isVisible;
    }
  }, [isVisible]);

  // Memoize style object to prevent unnecessary re-renders that cause blinking
  const iframeStyle = React.useMemo(() => ({
    border: 'none',
    outline: 'none',
    position: 'fixed',
    padding: 0,
    margin: 0,
    bottom: '0px',
    right: '0px',
    zIndex: 1000,
    width: isExpanded ? '560px' : '88px',
    height: isExpanded ? '650px' : '88px',
    background: 'transparent',
    pointerEvents: isVisible ? 'auto' : 'none',
    overflow: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    borderRadius: isExpanded ? '16px' : '50%',
    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1), height 0.5s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    visibility: isVisible ? 'visible' : 'hidden', opacity: isVisible ? 1 : 0
  }), [isExpanded, isVisible]);

  return (
    <iframe
      src="/floating-button-expandable.html"
      title="COO Chat Assistant"
      style={iframeStyle}
      ref={iframeRef}
    />
  );
};

export default COOFloatingButton;